<template>
    <v-theme-provider light>
        <div class="twofa-page">
            <div class="twofa-card">
                <div class="logo">
                    <v-img :width="logoWidth" :height="logoHeight" :src="logoSrc" contain />
                </div>

                <h3 class="twofa-title">Two-Factor Authentication</h3>

                <p class="twofa-sub">
                    Enter the 6-digit code from your Authenticator app to continue.
                </p>

                <v-form class="twofa-form">
                    <fieldset class="twofa-fieldset">
                        <legend>OTP Code</legend>

                        <v-text-field v-model="otp" placeholder="123456" single-line solo hide-details
                            class="custom-select twofa-input" maxlength="6"
                            @input="otp = (otp || '').replace(/[^0-9]/g, '').slice(0, 6)" @keyup.enter="submit" />
                    </fieldset>

                    <v-btn class="ma-0 text-capitalize btn-account mt-4" color="#039DE9" tile block elevation="0"
                        :loading="loading" :disabled="loading || String(otp).length !== 6" @click.prevent="submit">
                        Verify & Continue
                    </v-btn>

                    <v-btn class="ma-0 text-capitalize btn-account mt-3" tile block elevation="0" outlined
                        color="#172233" @click.prevent="cancel">
                        Cancel
                    </v-btn>
                </v-form>
            </div>
        </div>
    </v-theme-provider>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { PUBLIC_LOGIN__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";

export default {
    name: "TwoFaPage",
    data: () => ({
        otp: "",
        loading: false,
    }),

    computed: {
        ...mapState("publicinfo", ["info"]),
        ...mapState("authentication", ["twoFaLogin"]),

        // always use light logo for this page
        logoSrc() {
            return this.info?.logo_url;
        },
        logoWidth() {
            return this.info?.logo_width || "160";
        },
        logoHeight() {
            return this.info?.logo_height || "60";
        },
    },

    methods: {
        ...mapActions("publicinfo", [PUBLIC_LOGIN__ACTIONS.LOGINPAGEINFO]),
        ...mapActions("authentication", {
            VERIFY_TWOFA_LOGIN: "VERIFY_TWOFA_LOGIN",
        }),

        async submit() {
            this.loading = true;
            try {
                const ok = await this.VERIFY_TWOFA_LOGIN({
                    otp: this.otp,
                    two_fa_token: this.twoFaLogin?.two_fa_token,
                });

                if (ok) {
                    this.otp = "";
                    this.$router.push("/dashboard");
                }
            } catch (e) {
                toast.error(e?.response?.data?.message || "Verification failed");
            } finally {
                this.loading = false;
            }
        },

        cancel() {
            this.$store.commit("authentication/CLEAR_TWOFA_LOGIN");
            this.$router.push("/");
        },
    },

    async mounted() {
        await this[PUBLIC_LOGIN__ACTIONS.LOGINPAGEINFO]();

        if (!this.twoFaLogin?.two_fa_token) {
            this.$router.push("/");
        }
    },
};
</script>

<style scoped>
/* always light page */
.twofa-page {
    min-height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: #f1f7fb;
}

.twofa-card {
    width: 100%;
    max-width: 520px;
    padding: 28px 26px;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(16, 24, 40, 0.08);
    text-align: center;
}

.logo {
    display: flex;
    justify-content: center;
    margin-bottom: 14px;
}

.twofa-title {
    margin: 6px 0 8px;
    font-size: 22px;
    font-weight: 700;
    color: #172233;
}

.twofa-sub {
    margin: 0 0 18px;
    font-size: 14px;
    line-height: 1.5;
    color: #708aa7;
}

.twofa-form {
    text-align: left;
}

.twofa-fieldset {
    border: 1px solid rgba(112, 138, 167, 0.25);
    border-radius: 10px;
    padding: 10px 12px 14px;
}

.twofa-fieldset legend {
    padding: 0 8px;
    font-size: 12px;
    color: #708aa7;
}

/* FORCE light input (works even if global .custom-select is dark) */
::v-deep .twofa-input.v-text-field--solo .v-input__slot {
    background: #ffffff !important;
    color: #172233 !important;
    border-radius: 8px !important;
}

::v-deep .twofa-input input {
    color: #172233 !important;
}

::v-deep .twofa-input input::placeholder {
    color: rgba(23, 34, 51, 0.45) !important;
}

.twofa-page {
    width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
}
</style>
